import { useEffect, useState } from 'react';
import { fmtDur, ldnCompose, londonDateStr } from '@foodstyles/shared';
import { useBootstrap } from '../../hooks/useBootstrap';
import { useToast } from '../../context/ToastContext';
import { useAddManualEntry } from '../../hooks/useEntries';
import { Btn, Field, Modal, TextInput } from '../../components/primitives';
import { TaskCombobox } from '../../components/TaskCombobox';
import { ApiError } from '../../lib/api';

export function AddTimeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const boot = useBootstrap();
  const toast = useToast();
  const add = useAddManualEntry();
  const [memberId, setMemberId] = useState('');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(() => londonDateStr(0));
  const [startT, setStartT] = useState('09:00');
  const [stopT, setStopT] = useState('17:00');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setMemberId('');
      setProjectId(null);
      setTaskId(null);
      setDescription('');
      setDate(londonDateStr(0));
      setStartT('09:00');
      setStopT('17:00');
      setErr('');
    }
  }, [open]);

  const members = (boot.data?.users ?? []).filter((u) => u.status === 'Active');
  const projects = boot.data?.projects ?? [];
  const tasks = boot.data?.tasks ?? [];
  const preview = fmtDur(ldnCompose(date, stopT) - ldnCompose(date, startT));

  const save = () => {
    if (!memberId) return setErr('Pick a member.');
    if (!description.trim()) return setErr('Description is mandatory.');
    if (!taskId) return setErr('Task is mandatory.');
    const start = ldnCompose(date, startT);
    const stop = ldnCompose(date, stopT);
    if (stop <= start) return setErr('Stop time must be after start time.');
    if (start > Date.now()) return setErr("Can't add time in the future.");
    setErr('');
    add.mutate(
      { ownerId: memberId, projectId, taskId, description: description.trim(), start, stop },
      {
        onSuccess: () => onClose(),
        onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not add time.'),
      },
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="Add time for a member">
      <div className="space-y-4">
        <p className="rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
          Admin only. The entry is recorded in the audit log as added by you.
        </p>
        <Field label="Member">
          <select
            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
          >
            <option value="">Pick a member…</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Task">
          <TaskCombobox
            projects={projects}
            tasks={tasks}
            projectId={projectId}
            taskId={taskId}
            disabled={!memberId}
            onPick={({ projectId: p, taskId: t }) => {
              setProjectId(p || null);
              setTaskId(t || null);
            }}
          />
        </Field>
        <Field label="Description">
          <TextInput value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Date">
            <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Start (UK)">
            <TextInput type="time" value={startT} onChange={(e) => setStartT(e.target.value)} />
          </Field>
          <Field label="Stop (UK)">
            <TextInput type="time" value={stopT} onChange={(e) => setStopT(e.target.value)} />
          </Field>
        </div>
        <p className="text-right text-xs text-slate-400">Duration: {preview}</p>
        {err && <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>}
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="primary" onClick={save} disabled={add.isPending}>
            Add time
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
